import { RouteProp, useRoute } from '@react-navigation/native';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import {
	JackpotChipPng,
	PlayerIconPng,
	RankingsBronzePng,
	RankingsChipsPng,
	RankingsDiamondPng,
	RankingsFirstPlacePng,
	RankingsGoldPng,
	RankingsHeroBannerDesktopJpg,
	RankingsHeroBannerJpg,
	RankingsPlatinumPng,
	RankingsPointsPng,
	RankingsRichestPng,
	RankingsSecondPlacePng,
	RankingsSilverPng,
	RankingsStarPng,
	RankingsThirdPlacePng,
	RankingsTournamentsPng,
} from '../../assets';
import { DuelSwordsJpg } from '../../assets/duels';
// A PNG, not an inlined SVG: on web this screen stays mounted under the next one, and an SVG's ids
// would capture every crown the Social screen draws (see FriendCard).
import CrownPng from '../../assets/social/crown.png';
import { selectIsLogged, selectProfile } from '../../store/auth/authReducer';
import { duelsRankingRequest } from '../../store/duels/duelsActions';
import { DuelRankingScope, selectDuelRanking } from '../../store/duels/duelsReducer';
import {
	rankingsByPeriodRequest,
	rankingsRequest,
	rankingsTournamentsRequest,
} from '../../store/rankings/rankingsActions';
import {
	RankingItem,
	selectRankItems,
	selectRankLoading,
} from '../../store/rankings/rankingsReducer';
import { getAvatarUrl } from '../../utils/axiosInstance';
import { useSafeViewport } from '../../utils/hooks/useSafeViewport';
import { getNavRowHeight } from '../Components/AppFooter/Nav/FooterNavButtonsV2';
import AfterFirstFrame from '../Components/common/AfterFirstFrame';
import GradientPlate, { GradientStops } from '../Components/common/GradientPlate';
import { IndicatorScrollView } from '../Components/common/ScrollIndicator';
import { Pressable } from '../Components/common/Touchable';
import { desktopScrollFade, useDesktopLayout } from '../Components/Desktop/desktopLayout';
import { duelText } from '../Components/Duels/duelsText';
import { HEADER_BACK_ROW_HEIGHT } from '../Components/Top/TopSection';
import { RootStackParamList } from '../navigator';
import { useHeaderTopOffset } from '../Pages/headerOffset';
import { MainLayout } from '../Pages/MainLayout';

const DESIGN_WIDTH = 390;
const CONTENT_WIDTH = 366;
const MAX_SCALE = 1.15;
/**
 * Figma "Rankings / Desktop — The Richest" (6883:18393, final "Desktop" section 6851:114959), on a
 * 1440 x 1024 frame whose desktop scale is 1.125: the banner 320 high (the frame runs it across the
 * 1368 content width; here it matches the table's 960); the tabs and the tier row 458 wide (here
 * they too match the table, with the tiers spread across it); the table 960. Corner radii scale with
 * them too. Those three are the mobile components at exactly 1.25x, and the frame spells its labels
 * in mixed case ("The Richest", "Bronze", "Total Chips").
 */
const DESKTOP_FRAME_SCALE = 1.125;
const DESKTOP_COMPONENT_SCALE = 1.25;
/** The category tabs and the row under them, widened to the table, at 1.5x the mobile ones. */
const DESKTOP_CONTROLS_SCALE = 1.5;
const DESKTOP = { controls: 960, table: 960 } as const;
/** Gaps on the frame: banner 449 to tabs 481, tabs to tiers 16, tiers 679.5 to table 703.5. */
const DESKTOP_GAP = { afterHero: 32, afterTabs: 16, afterTiers: 24 } as const;

/** "SVARA CLUB" -> "Svara Club": the redesign spells every label in mixed case. */
const titleCase = (text: string) => text.toLowerCase().replace(/(^|\s)\S/g, c => c.toUpperCase());
/**
 * The category bar and its pill (in mobile units), taller than the frame's 62 / 54 so a label can
 * wrap to a second line under a bigger icon. The desktop frame's bar stays 2 taller, as it was
 * (64 / 56), and its active tier fills the 82 row (85 x 102.5).
 */
const CATEGORY_TAB = {
	tabsHeight: 78,
	tabHeight: 70,
	icon: [32, 30],
	divider: 40,
	padding: 2,
} as const;
const DESKTOP_CONTROLS = { tabsHeight: 80, tabHeight: 72, activeTierHeight: 82 } as const;

type RankingRow = RankingItem & { club: string; rank: number; isYou?: boolean };
type LeagueKey = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
type RankingCategory = 'richest' | 'tournaments' | 'earned' | 'points' | 'duels';
type RankingPeriod = 'Daily' | 'Weekly' | 'Monthly';
type TournamentPeriod = 'Month' | 'All Time';
type RankingSeason = 'Current Season' | 'Previous Seasons';

const SEGMENT_LABEL_KEYS: Record<string, string> = {
	Daily: 'T_SITE_RANKING_TAB_DAILY',
	Weekly: 'T_SITE_RANKING_TAB_WEEKLY',
	Monthly: 'T_SITE_RANKING_TAB_MONTHLY',
	Month: 'T_SITE_RANKING_TAB_MONTH',
	'All Time': 'T_SITE_RANKING_TAB_ALL_TIME',
	'Current Season': 'T_SITE_RANKING_CURRENT_SEASON',
	'Previous Seasons': 'T_SITE_RANKING_PREVIOUS_SEASONS',
};

const LEAGUES = [
	{
		key: 'bronze' as const,
		name: 'Bronze',
		nameKey: 'T_SITE_TIER_BRONZE',
		range: '0 – 100K',
		icon: RankingsBronzePng,
		min: 0,
		max: 100000,
	},
	{
		key: 'silver' as const,
		name: 'Silver',
		nameKey: 'T_SITE_TIER_SILVER',
		range: '100K – 1M',
		icon: RankingsSilverPng,
		min: 100000,
		max: 1000000,
	},
	{
		key: 'gold' as const,
		name: 'Gold',
		nameKey: 'T_SITE_TIER_GOLD',
		range: '1M – 10M',
		icon: RankingsGoldPng,
		min: 1000000,
		max: 10000000,
	},
	{
		key: 'platinum' as const,
		name: 'Platinum',
		nameKey: 'T_SITE_TIER_PLATINUM',
		range: '10M – 100M',
		icon: RankingsPlatinumPng,
		min: 10000000,
		max: 100000000,
	},
	{
		key: 'diamond' as const,
		name: 'Diamond',
		nameKey: 'T_SITE_TIER_DIAMOND',
		range: '100M+',
		icon: RankingsDiamondPng,
		min: 100000000,
	},
];

const DEMO_ROWS: RankingRow[] = [
	{
		id: 'demo-1',
		rank: 1,
		username: 'MAD BULL',
		club: 'SOFIA CLUB',
		level: 1,
		isVip: true,
		totalChips: 9824500,
		exp: 0,
	},
	{
		id: 'demo-2',
		rank: 2,
		username: 'THE KID',
		club: 'MONACO CLUB',
		level: 1,
		totalChips: 8753200,
		exp: 0,
	},
	{
		id: 'demo-3',
		rank: 3,
		username: 'MR. POKER',
		club: 'LONDON CLUB',
		level: 1,
		totalChips: 7631900,
		exp: 0,
	},
	{
		id: 'demo-4',
		rank: 4,
		username: 'HIGH ROLLER',
		club: 'PARIS CLUB',
		level: 1,
		totalChips: 6512800,
		exp: 0,
	},
	{
		id: 'demo-5',
		rank: 5,
		username: 'LUCKY LADY',
		club: 'VEGAS CLUB',
		level: 1,
		totalChips: 5830700,
		exp: 0,
	},
	{
		id: 'demo-6',
		rank: 6,
		username: 'ACE PLAYER',
		club: 'MACAO CLUB',
		level: 1,
		totalChips: 4926800,
		exp: 0,
	},
	{
		id: 'demo-7',
		rank: 7,
		username: 'BLACK JACK',
		club: 'SOFIA CLUB',
		level: 1,
		totalChips: 4375400,
		exp: 0,
	},
	{
		id: 'demo-8',
		rank: 8,
		username: 'GOLDEN BOY',
		club: 'MONACO CLUB',
		level: 1,
		totalChips: 3892100,
		exp: 0,
	},
	{
		id: 'demo-self',
		rank: 128,
		username: 'YOU',
		club: 'SOFIA CLUB',
		level: 1,
		totalChips: 1240000,
		exp: 0,
	},
];

const formatChips = (value: number) => `${Math.round(value).toLocaleString('en-US')}`;

const RankingScreen = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const items = useSelector(selectRankItems) as RankingItem[];
	const isLoading = useSelector(selectRankLoading) as boolean;
	const { width, height, insets } = useSafeViewport();
	const [selectedLeague, setSelectedLeague] = useState<LeagueKey>('gold');
	const [selectedCategory, setSelectedCategory] = useState<RankingCategory>('richest');
	const [selectedPeriod, setSelectedPeriod] = useState<RankingPeriod>('Weekly');
	const [tournamentPeriod, setTournamentPeriod] = useState<TournamentPeriod>('Month');
	const [selectedSeason, setSelectedSeason] = useState<RankingSeason>('Current Season');
	const [duelScope, setDuelScope] = useState<DuelRankingScope>('current');
	const { params } = useRoute<RouteProp<RootStackParamList, 'Ranking'>>();
	const isLogged = useSelector(selectIsLogged);
	const user = useSelector(selectProfile);
	const duelBoard = useSelector(selectDuelRanking(duelScope));
	const scale = Math.min(width / DESIGN_WIDTH, MAX_SCALE);
	const contentWidth = CONTENT_WIDTH * scale;
	const footerClearance = getNavRowHeight(width, height, true) + insets.bottom;
	const desktop = useDesktopLayout();

	useEffect(() => {
		if (params?.category === 'duels') setSelectedCategory('duels');
	}, [params]);

	useEffect(() => {
		if (selectedCategory === 'duels') {
			if (isLogged) dispatch(duelsRankingRequest(duelScope));
			return;
		}
		// No endpoint serves tournament wins yet; the tab shows its empty state.
		if (selectedCategory === 'tournaments') return;
		if (selectedCategory === 'earned') {
			dispatch(rankingsByPeriodRequest({ period: selectedPeriod, _start: 0, _end: 100 }));
			return;
		}
		if (selectedCategory === 'points') {
			dispatch(rankingsTournamentsRequest({ _start: 0, _end: 100 }));
			return;
		}

		dispatch(rankingsRequest({ _start: 0, _end: 100 }));
	}, [dispatch, selectedCategory, selectedPeriod, duelScope, isLogged]);

	// Tournament wins and past seasons have no API behind them yet.
	const hasNoData =
		selectedCategory === 'tournaments' ||
		(selectedCategory === 'points' && selectedSeason === 'Previous Seasons');
	// Event points read the backend's one tournament leaderboard; there is no separate event-points
	// endpoint.
	const showsTournamentPoints = selectedCategory === 'points';

	const rows = useMemo<RankingRow[]>(() => {
		if (selectedCategory === 'duels') {
			return (duelBoard.board?.items ?? []).map(item => ({
				id: item.playerId,
				username: item.username ?? '—',
				level: 0,
				exp: 0,
				totalChips: item.points,
				avatarFileId: item.avatarFileId,
				rank: item.position,
				club:
					item.divisionLabel && item.rating !== null
						? duelText(t, 'T_SITE_DUELS_DIVISION_RATING', [
								item.divisionLabel,
								formatChips(item.rating),
							])
						: '',
				isYou: String(item.playerId) === String(user?.id),
			}));
		}
		if (hasNoData) return [];
		const source = items.length
			? items.map((item, index) => ({
					...item,
					totalChips: showsTournamentPoints ? (item.weeklyTournamentPoints ?? 0) : item.totalChips,
					rank: index + 1,
					club: 'Svara Club',
				}))
			: // Nothing while the first list is on its way: the demo rows used to show for that
				// moment and then swap for the real players as the screen faded in.
				isLoading
				? []
				: DEMO_ROWS;
		if (selectedCategory !== 'richest')
			return source.map((row, index) => ({ ...row, rank: index + 1 }));

		const league = LEAGUES.find(item => item.key === selectedLeague)!;
		return source
			.filter(
				row =>
					row.totalChips >= league.min && (league.max === undefined || row.totalChips < league.max),
			)
			.map((row, index) => ({ ...row, rank: index + 1 }));
	}, [
		items,
		isLoading,
		selectedCategory,
		selectedLeague,
		hasNoData,
		showsTournamentPoints,
		duelBoard.board,
		t,
		user?.id,
	]);

	const metricLabel = titleCase(
		selectedCategory === 'earned'
			? t('T_SITE_RANKING_CHIPS_EARNED', { defaultValue: 'Chips Earned' })
			: selectedCategory === 'points'
				? t('T_SITE_RANKING_EVENT_POINTS', { defaultValue: 'Event Points' })
				: selectedCategory === 'tournaments'
					? t('T_SITE_RANKING_TOURNAMENT_WINS', { defaultValue: 'Tournament Wins' })
					: selectedCategory === 'duels'
						? duelText(t, 'T_SITE_DUELS_SEASON_PTS')
						: t('T_SITE_RANKING_TOTAL_CHIPS', { defaultValue: 'Total Chips' }),
	);
	const valueIcon =
		selectedCategory === 'points' || selectedCategory === 'duels'
			? RankingsStarPng
			: selectedCategory === 'tournaments'
				? RankingsTournamentsPng
				: JackpotChipPng;
	const duelEmptyText = (() => {
		if (!isLogged) return duelText(t, 'T_SITE_DUELS_RANKING_LOGIN_REQUIRED');
		if (duelBoard.errorKey) return duelText(t, duelBoard.errorKey);
		if (!duelBoard.loaded) return '';
		if (!duelBoard.board) return duelText(t, 'T_SITE_DUELS_NO_PREVIOUS_SEASON');
		return duelText(t, 'T_SITE_DUELS_RANKING_EMPTY');
	})();
	const duelOwnPosition = duelBoard.board?.own.position ?? null;
	const duelNote = (() => {
		if (selectedCategory !== 'duels' || !duelBoard.board) return null;
		const season = duelBoard.board.season?.name ?? '';
		const caption =
			duelScope === 'previous'
				? duelText(
						t,
						duelBoard.board.final
							? 'T_SITE_DUELS_FINAL_STANDINGS'
							: 'T_SITE_DUELS_STANDINGS_PENDING',
						[season],
					)
				: null;
		const own =
			duelOwnPosition && !rows.some(row => row.isYou)
				? duelText(t, 'T_SITE_DUELS_YOUR_POSITION', [duelOwnPosition])
				: null;
		return [caption, own].filter(Boolean).join('\n') || null;
	})();
	const emptyText =
		selectedCategory === 'duels'
			? duelEmptyText
			: selectedCategory === 'tournaments'
				? t('T_SITE_RANKING_EMPTY_TOURNAMENTS', { defaultValue: 'No tournament results yet' })
				: selectedCategory === 'points' && selectedSeason === 'Previous Seasons'
					? t('T_SITE_RANKING_EMPTY_SEASONS', { defaultValue: 'No previous seasons yet' })
					: t('T_SITE_RANKING_EMPTY_DIVISION', {
							defaultValue: 'No players in this division yet',
						});
	// "Tournament Wins" counts are a couple of digits: the trophy sits next to them.
	const valueWidth = selectedCategory === 'tournaments' ? 28 : 74;
	// The mobile frames rim the richest table in gold and the others in border/red; the final desktop
	// frames use border/red on every tab.
	const tableBorder = selectedCategory === 'richest' ? '#FCF17B' : '#6B1A10';
	const desktopTableBorder = '#6B1A10';

	// The league / period / season row under the category tabs.
	const renderSubControls = (controlsScale: number, controlsWidth: number, desktopFrame = false) =>
		selectedCategory === 'richest' ? (
			<LeagueCarousel
				scale={controlsScale}
				width={controlsWidth}
				value={selectedLeague}
				onChange={setSelectedLeague}
				desktopFrame={desktopFrame}
			/>
		) : selectedCategory === 'tournaments' ? (
			<SegmentTabs
				scale={controlsScale}
				width={controlsWidth}
				options={['Month', 'All Time'] as TournamentPeriod[]}
				value={tournamentPeriod}
				onChange={setTournamentPeriod}
			/>
		) : selectedCategory === 'duels' ? (
			<SegmentTabs
				scale={controlsScale}
				width={controlsWidth}
				options={['Current Season', 'Previous Seasons'] as RankingSeason[]}
				value={duelScope === 'current' ? 'Current Season' : 'Previous Seasons'}
				onChange={season => setDuelScope(season === 'Current Season' ? 'current' : 'previous')}
			/>
		) : selectedCategory === 'earned' ? (
			<SegmentTabs
				scale={controlsScale}
				width={controlsWidth}
				options={['Daily', 'Weekly', 'Monthly'] as RankingPeriod[]}
				value={selectedPeriod}
				onChange={setSelectedPeriod}
			/>
		) : (
			<SegmentTabs
				scale={controlsScale}
				width={controlsWidth}
				options={['Current Season', 'Previous Seasons'] as RankingSeason[]}
				value={selectedSeason}
				onChange={setSelectedSeason}
			/>
		);

	const renderDuelNote = (noteScale: number, noteWidth: number) =>
		duelNote ? (
			<Text style={[styles.duelNote, { width: noteWidth, fontSize: 12 * noteScale }]}>
				{duelNote}
			</Text>
		) : null;

	// The banner, the category tabs and the row under them.
	const renderControls = (controlsScale: number, controlsWidth: number) => (
		<>
			<RankingsHero scale={controlsScale} width={controlsWidth} />
			<CategoryTabs
				scale={controlsScale}
				width={controlsWidth}
				value={selectedCategory}
				onChange={setSelectedCategory}
			/>
			{renderSubControls(controlsScale, controlsWidth)}
			{renderDuelNote(controlsScale, controlsWidth)}
		</>
	);

	if (desktop.isDesktopLayout) {
		// The whole page scrolls as one, as the frame is laid out: under fixed controls the desktop
		// content block only had room for two or three rows.
		const u = desktop.scale / DESKTOP_FRAME_SCALE;
		const k = DESKTOP_COMPONENT_SCALE * u;
		// The rows under the banner span the table, so their icons and labels grow past the frame's 1.25x.
		const controlsK = DESKTOP_CONTROLS_SCALE * u;
		const controlsWidth = DESKTOP.controls * u;

		return (
			<MainLayout fullBleed>
				<AfterFirstFrame>
					<IndicatorScrollView
						style={[styles.desktopScroll, desktopScrollFade(desktop.s(40))]}
						contentContainerStyle={[
							styles.desktopColumn,
							{ paddingTop: 3 * u, paddingBottom: desktop.s(40) },
						]}
						showsVerticalScrollIndicator={false}
						// Centred on the frame like the file; the bar runs in the content box's side margin.
						reserveGutter={false}
					>
						{/* The banner lines up with the table under it rather than the frame's 1368 content
					    box, at the frame's 320 height; the art still covers it (960 wide, it is 407 high). */}
						<RankingsHero
							scale={u}
							width={DESKTOP.table * u}
							height={HERO_VARIANTS.desktop.height * u}
							variant='desktop'
						/>
						<View style={{ marginTop: DESKTOP_GAP.afterHero * u }}>
							<CategoryTabs
								scale={controlsK}
								width={controlsWidth}
								value={selectedCategory}
								onChange={setSelectedCategory}
								desktopFrame
							/>
						</View>
						<View style={{ marginTop: DESKTOP_GAP.afterTabs * u }}>
							{renderSubControls(controlsK, controlsWidth, true)}
							{renderDuelNote(controlsK, controlsWidth)}
						</View>
						<RankingTable
							rows={rows}
							scale={k}
							width={DESKTOP.table * u}
							scrolls={false}
							metricLabel={metricLabel}
							valueIcon={valueIcon}
							emptyText={emptyText}
							borderColor={desktopTableBorder}
							valueWidth={valueWidth}
							style={{ marginTop: DESKTOP_GAP.afterTiers * u }}
						/>
					</IndicatorScrollView>
				</AfterFirstFrame>
			</MainLayout>
		);
	}

	return (
		<MainLayout fullBleed>
			<AfterFirstFrame>
				<View style={styles.screen}>
					<View style={[styles.fixedContent, { width: contentWidth, rowGap: 10 * scale }]}>
						<HeaderClearance />
						{renderControls(scale, contentWidth)}
					</View>
					<RankingTable
						rows={rows}
						scale={scale}
						width={contentWidth}
						style={{ marginTop: 10 * scale, marginBottom: footerClearance + 16 * scale }}
						metricLabel={metricLabel}
						valueIcon={valueIcon}
						emptyText={emptyText}
						borderColor={tableBorder}
						valueWidth={valueWidth}
					/>
				</View>
			</AfterFirstFrame>
		</MainLayout>
	);
};

/**
 * Room for the top bar; the stack's 10 row gap then starts the banner 10 under it, as the design
 * does. The header is not scaled with the frame and each layout hosts it somewhere else - under
 * the status-bar inset on native, HeaderTopOffsetContext down on mobile web - so the clearance
 * follows it rather than the 844 frame, which put the banner under the bar on a notched phone.
 * It is its own component because that context is only provided inside MainLayout.
 */
const HeaderClearance = () => {
	const { insets } = useSafeViewport();
	const headerTopOffset = useHeaderTopOffset();
	return <View style={{ height: insets.top + headerTopOffset + HEADER_BACK_ROW_HEIGHT }} />;
};

/**
 * The banner, "Hero / Rankings": the trophy art in a rounded frame with a border/gold rim, a
 * dark-red fade from the middle to the right edge, and the title and tagline right-aligned over the
 * fade. Mobile is 6895:117592 (366 x 140); desktop is the final desktop frame's 6919:117651 (1368 x
 * 320 on the 1440 frame), the same art cropped to cover the wider box.
 */
const HERO_VARIANTS = {
	mobile: {
		width: 366,
		height: 140,
		radius: 12,
		rim: 1,
		copyRight: 24,
		copyGap: 8,
		title: [24, 28],
		tagline: [14, 20],
	},
	desktop: {
		width: 1368,
		height: 320,
		radius: 16,
		rim: 4,
		copyRight: 32,
		copyGap: 16,
		title: [56, 64],
		tagline: [24, 32],
	},
} as const;
/** The art's own shape (1584 x 672). */
const HERO_ART_ASPECT = 1584 / 672;
/** "Text fade": #3A0000 across, at these offsets and opacities. */
const HERO_FADE: Array<[string, number]> = [
	['0', 0],
	['0.23558', 0],
	['0.38942', 0.18],
	['0.47596', 0.42],
	['0.55288', 0.62],
	['0.75962', 0.75],
	['1', 0.75],
];

/**
 * `scale` maps the variant's design units to the screen; `width` is the box's real width, and the
 * height keeps the variant's shape unless `height` is given.
 */
const RankingsHero = ({
	scale,
	width,
	height: boxHeight,
	variant = 'mobile',
}: {
	scale: number;
	width: number;
	height?: number;
	variant?: keyof typeof HERO_VARIANTS;
}) => {
	const { t } = useTranslation();
	const v = HERO_VARIANTS[variant];
	const radius = v.radius * scale;
	const height = boxHeight ?? (width * v.height) / v.width;
	// Covers the box and is centred in it, as the files crop it. On mobile the art sits 1 lower and
	// runs past the bottom (367 x 147 on the 366 x 140 frame).
	const artHeight = variant === 'mobile' ? (width * 147) / v.width : width / HERO_ART_ASPECT;
	const artTop = variant === 'mobile' ? 1 * scale : (height - artHeight) / 2;

	return (
		<View
			style={[
				styles.heroFrame,
				{ width, height, borderRadius: radius, borderWidth: v.rim * scale },
			]}
		>
			{/* Sized explicitly: on iOS an absolutely filled Image kept the art's own size. Placed
			    against the outer edge, so the rim draws over the art rather than insetting it. */}
			<Image
				source={variant === 'mobile' ? RankingsHeroBannerJpg : RankingsHeroBannerDesktopJpg}
				resizeMode='cover'
				style={[
					styles.heroImage,
					{
						left: -v.rim * scale,
						top: artTop - v.rim * scale,
						width,
						height: artHeight,
					},
				]}
			/>
			<View pointerEvents='none' style={StyleSheet.absoluteFill}>
				<Svg width='100%' height='100%'>
					<Defs>
						<LinearGradient id='rankingsHeroFade' x1='0' y1='0' x2='1' y2='0'>
							{HERO_FADE.map(([offset, opacity]) => (
								<Stop key={offset} offset={offset} stopColor='#3A0000' stopOpacity={opacity} />
							))}
						</LinearGradient>
					</Defs>
					<Rect x='0' y='0' width='100%' height='100%' fill='url(#rankingsHeroFade)' />
				</Svg>
			</View>
			<View
				pointerEvents='none'
				style={[
					styles.heroCopy,
					{ right: (v.copyRight - v.rim) * scale, rowGap: v.copyGap * scale },
				]}
			>
				<Text
					style={[
						styles.heroTitleText,
						{ fontSize: v.title[0] * scale, lineHeight: v.title[1] * scale },
					]}
				>
					{t('T_SITE_RANKING_TITLE', { defaultValue: 'RANKINGS' }).toUpperCase()}
				</Text>
				<Text
					style={[
						styles.heroTagline,
						{ fontSize: v.tagline[0] * scale, lineHeight: v.tagline[1] * scale },
					]}
				>
					{t('T_SITE_RANKING_TAGLINE', {
						defaultValue: 'Climb the leaderboard.\nProve your skills',
					})}
				</Text>
			</View>
		</View>
	);
};

const ACTIVE_TAB_FILL: GradientStops = [
	['0', '#E0230F'],
	['1', '#8C0D05'],
];
/** "Tier / Gold (Active)": the selected league's tile. */
const ACTIVE_TIER_FILL: GradientStops = [
	['0', '#4A1008'],
	['1', '#1E0603'],
];

/**
 * A gradient plate with its rim as one SVG shape sized to its parent - the selected category, segment
 * and league. A CSS border around a separately rounded SVG fill stepped around the corners at 1x and
 * left the rim looking bitten where the two curves met.
 */
const FilledPlate = ({
	radius,
	stops = ACTIVE_TAB_FILL,
	stroke = '#FF6A4D',
	strokeWidth = 1,
}: {
	radius: number;
	stops?: GradientStops;
	stroke?: string;
	strokeWidth?: number;
}) => {
	const [size, setSize] = useState({ width: 0, height: 0 });
	return (
		<View
			pointerEvents='none'
			style={StyleSheet.absoluteFill}
			onLayout={event => {
				const { width, height } = event.nativeEvent.layout;
				setSize(prev =>
					prev.width === width && prev.height === height ? prev : { width, height },
				);
			}}
		>
			{size.width > 0 ? (
				<GradientPlate
					width={size.width}
					height={size.height}
					radius={radius}
					stops={stops}
					stroke={stroke}
					strokeWidth={strokeWidth}
				/>
			) : null}
		</View>
	);
};

const CATEGORY_TABS = [
	{
		label: 'The Richest',
		labelKey: 'T_SITE_RANKING_RICHEST',
		icon: RankingsRichestPng,
		category: 'richest' as const,
	},
	{
		label: 'Tournaments',
		labelKey: 'T_SITE_RANKING_TOURNAMENTS',
		icon: RankingsTournamentsPng,
		category: 'tournaments' as const,
	},
	{
		label: 'Earned Chips',
		labelKey: 'T_SITE_RANKING_EARNED_CHIPS',
		icon: RankingsChipsPng,
		category: 'earned' as const,
	},
	{
		label: 'Event Points',
		labelKey: 'T_SITE_RANKING_EVENT_POINTS',
		icon: RankingsPointsPng,
		category: 'points' as const,
	},
	{
		label: 'Duels',
		labelKey: 'T_SITE_DUELS_TITLE',
		icon: DuelSwordsJpg,
		category: 'duels' as const,
	},
];

/**
 * The pieces of a label that have to fit on a line of their own: its words, with a hyphenated word
 * split after the hyphen, where text may wrap ("Най-" / "богатите").
 */
const labelWords = (label: string) =>
	label
		.split(/\s+/)
		.flatMap(word => word.replace(/-/g, '-\n').split('\n'))
		.filter(Boolean);

const CategoryTabs = ({
	scale,
	width,
	value,
	onChange,
	desktopFrame = false,
}: {
	scale: number;
	width: number;
	value: RankingCategory;
	onChange: (category: RankingCategory) => void;
	desktopFrame?: boolean;
}) => {
	const { t } = useTranslation();
	const activeIndex = CATEGORY_TABS.findIndex(tab => tab.category === value);
	const labels = CATEGORY_TABS.map(tab => titleCase(t(tab.labelKey, { defaultValue: tab.label })));
	const fontSize = 12 * scale;
	// Each word's one-line width at the full size, from the hidden copies below.
	const [wordWidths, setWordWidths] = useState<Record<string, number>>({});
	// The tabs share the bar evenly; a word wider than its tab's text box shrinks that tab's label
	// rather than breaking mid-word ("Tournaments", "Заработанные", "D'événement"). The slot loses
	// the bar's padding and rim, a divider and the tab's own padding.
	const padding = 4 * scale;
	const textWidth =
		(width - 2 * padding - 2) / CATEGORY_TABS.length - 1 - 2 * CATEGORY_TAB.padding * scale;
	const labelScale = (label: string) => {
		const widest = Math.max(0, ...labelWords(label).map(word => wordWidths[word] ?? 0));
		// 3% short of the box: shrunk to its exact width, "D'événement" still broke at the apostrophe.
		return widest > textWidth ? (textWidth * 0.97) / widest : 1;
	};
	const measureWords = [...new Set(labels.flatMap(labelWords))];

	return (
		<View
			style={[
				styles.tabs,
				{
					width,
					height: (desktopFrame ? DESKTOP_CONTROLS.tabsHeight : CATEGORY_TAB.tabsHeight) * scale,
					padding,
					borderRadius: 10 * scale,
				},
			]}
		>
			<View pointerEvents='none' style={styles.tabMeasure}>
				{measureWords.map(word => (
					<Text
						key={word}
						numberOfLines={1}
						style={[styles.tabLabel, { fontSize }]}
						onLayout={event => {
							const measured = event.nativeEvent.layout.width;
							setWordWidths(prev =>
								prev[word] === measured ? prev : { ...prev, [word]: measured },
							);
						}}
					>
						{word}
					</Text>
				))}
			</View>
			<View pointerEvents='none' style={StyleSheet.absoluteFill}>
				<Svg width='100%' height='100%'>
					<Defs>
						<LinearGradient id='rankingsTabsSurface' x1='0' y1='0' x2='0' y2='1'>
							<Stop offset='0' stopColor='#330B05' />
							<Stop offset='1' stopColor='#0F0402' />
						</LinearGradient>
					</Defs>
					<Rect x='0' y='0' width='100%' height='100%' fill='url(#rankingsTabsSurface)' />
				</Svg>
			</View>
			{CATEGORY_TABS.map((tab, index) => {
				const active = index === activeIndex;
				// "Dividers hide next to the active tab."
				const divider =
					index < CATEGORY_TABS.length - 1 && index !== activeIndex && index + 1 !== activeIndex;
				const label = labels[index];
				const fit = labelScale(label);
				return (
					<View key={tab.label} style={styles.tabSlot}>
						<Pressable
							onPress={() => onChange(tab.category)}
							accessibilityRole='button'
							accessibilityState={{ selected: active }}
							style={[
								styles.tab,
								active && styles.tabActive,
								{
									height:
										(desktopFrame ? DESKTOP_CONTROLS.tabHeight : CATEGORY_TAB.tabHeight) * scale,
									borderRadius: 8 * scale,
									paddingHorizontal: CATEGORY_TAB.padding * scale,
								},
							]}
						>
							{active ? <FilledPlate radius={8 * scale} /> : null}
							<Image
								source={tab.icon}
								resizeMode='contain'
								style={{
									width: CATEGORY_TAB.icon[0] * scale,
									height: CATEGORY_TAB.icon[1] * scale,
								}}
							/>
							{/* Two lines tall either way, so every icon sits at the same height; a one-line label
							    is centred in it. */}
							<View style={[styles.tabLabelBox, { height: 28 * scale }]}>
								<Text
									numberOfLines={2}
									style={[
										styles.tabLabel,
										{ fontSize: fontSize * fit, lineHeight: 14 * scale },
										active && styles.tabLabelActive,
									]}
								>
									{label}
								</Text>
							</View>
						</Pressable>
						{divider ? (
							<View style={[styles.tabDivider, { height: CATEGORY_TAB.divider * scale }]} />
						) : null}
					</View>
				);
			})}
		</View>
	);
};

/**
 * "Tabs / Period" and "Tabs / Season" (5297:14724): Segment / Period pills on the sunken surface -
 * Daily / Weekly / Monthly for earned chips, Month / All Time for tournaments, the seasons for
 * event points.
 */
const SegmentTabs = <T extends string>({
	scale,
	width,
	options,
	value,
	onChange,
}: {
	scale: number;
	width: number;
	options: T[];
	value: T;
	onChange: (option: T) => void;
}) => {
	const { t } = useTranslation();
	return (
		<View
			style={[
				styles.periodTabs,
				{ width, height: 36 * scale, padding: 4 * scale, borderRadius: 10 * scale },
			]}
		>
			{options.map(option => {
				const active = option === value;
				return (
					<Pressable
						key={option}
						onPress={() => onChange(option)}
						accessibilityRole='button'
						accessibilityState={{ selected: active }}
						style={[
							styles.periodTab,
							active && styles.tabActive,
							{ borderRadius: 8 * scale, paddingHorizontal: 6 * scale },
						]}
					>
						{active ? <FilledPlate radius={8 * scale} /> : null}
						<Text
							numberOfLines={1}
							style={[
								styles.periodLabel,
								active && styles.periodLabelActive,
								{ fontSize: 14 * scale, lineHeight: 16 * scale },
							]}
						>
							{titleCase(t(SEGMENT_LABEL_KEYS[option], { defaultValue: option }))}
						</Text>
					</Pressable>
				);
			})}
		</View>
	);
};

const LeagueCarousel = ({
	scale,
	width,
	value,
	onChange,
	desktopFrame = false,
}: {
	scale: number;
	width: number;
	value: LeagueKey;
	onChange: (league: LeagueKey) => void;
	desktopFrame?: boolean;
}) => {
	const { t } = useTranslation();
	return (
		<View
			style={[
				styles.leagues,
				{
					width,
					height: 82 * scale,
					// On desktop the row is wider than the five tiles, so they spread evenly across it.
					...(desktopFrame
						? { justifyContent: 'space-evenly' as const }
						: { paddingLeft: 15 * scale, gap: 2 * scale }),
					borderRadius: 12 * scale,
				},
			]}
		>
			<View pointerEvents='none' style={StyleSheet.absoluteFill}>
				<Svg width='100%' height='100%'>
					<Defs>
						<LinearGradient id='rankingsLeaguesSurface' x1='0' y1='0' x2='0' y2='1'>
							<Stop offset='0' stopColor='#330B05' />
							<Stop offset='1' stopColor='#0F0402' />
						</LinearGradient>
					</Defs>
					<Rect x='0' y='0' width='100%' height='100%' fill='url(#rankingsLeaguesSurface)' />
				</Svg>
			</View>
			{LEAGUES.map(league => {
				const active = league.key === value;
				const leagueName = t(league.nameKey, { defaultValue: league.name }).toUpperCase();
				return (
					<Pressable
						key={league.name}
						onPress={() => onChange(league.key)}
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_A11Y_SHOW_LEAGUE_PLAYERS', {
							defaultValue: 'Show {{league}} players',
							league: leagueName.toLowerCase(),
						})}
						accessibilityState={{ selected: active }}
						style={[
							styles.league,
							active && styles.leagueActive,
							{ borderRadius: 10 * scale },
							active
								? desktopFrame
									? {
											width: 68 * scale,
											// Inside the row's 1px rim: the row clips, and a tile as tall as the row lost its
											// bottom edge.
											height: DESKTOP_CONTROLS.activeTierHeight * scale - 2,
											marginTop: 0,
										}
									: { width: 68 * scale, height: 78 * scale, marginTop: 2 * scale }
								: { width: 65 * scale, height: 72 * scale, marginTop: 5 * scale },
						]}
					>
						{active ? (
							<FilledPlate
								radius={10 * scale}
								stops={ACTIVE_TIER_FILL}
								stroke='#FCF17B'
								strokeWidth={1.5}
							/>
						) : null}
						<Image
							source={league.icon}
							resizeMode='contain'
							style={{ width: 46 * scale, height: 46 * scale }}
						/>
						<Text
							style={[
								styles.leagueName,
								active && styles.leagueNameActive,
								// The desktop frame sets the name 2 lower under its 1.25x shield than -5 would.
								{ fontSize: 12 * scale },
								desktopFrame && { marginTop: -3 },
							]}
						>
							{titleCase(leagueName)}
						</Text>
						<Text
							style={[
								styles.leagueRange,
								active && styles.leagueRangeActive,
								{ fontSize: 10 * scale },
							]}
						>
							{league.range}
						</Text>
					</Pressable>
				);
			})}
		</View>
	);
};

/**
 * The leaderboard on the sunken surface. `scrolls={false}` lets the table grow to all its rows, for a
 * page that scrolls around it.
 */
const RankingTable = ({
	rows,
	scale,
	width,
	style,
	scrolls = true,
	metricLabel,
	valueIcon,
	emptyText,
	borderColor,
	valueWidth,
}: {
	rows: RankingRow[];
	scale: number;
	width: number;
	style?: object;
	scrolls?: boolean;
	metricLabel: string;
	valueIcon: number;
	emptyText: string;
	borderColor: string;
	/** The number's width beside the icon, in design units. */
	valueWidth: number;
}) => {
	const { t } = useTranslation();
	const body = (
		<RankingRows
			rows={rows}
			scale={scale}
			valueIcon={valueIcon}
			valueWidth={valueWidth}
			emptyText={emptyText}
		/>
	);
	return (
		<View
			style={[
				styles.table,
				!scrolls && styles.tableGrows,
				style,
				{
					width,
					borderColor,
					paddingHorizontal: 6 * scale,
					paddingVertical: 8 * scale,
					borderRadius: 12 * scale,
				},
			]}
		>
			<View
				style={[
					styles.tableHeader,
					{ height: 20 * scale, paddingLeft: 8 * scale, paddingRight: 12 * scale },
				]}
			>
				<Text
					style={[
						styles.headerText,
						styles.rankHeader,
						{ width: 24 * scale, fontSize: 16 * scale, lineHeight: 14 * scale },
					]}
				>
					#
				</Text>
				<Text
					style={[
						styles.headerText,
						styles.playerHeader,
						{ flex: 1, fontSize: 12 * scale, lineHeight: 14 * scale },
					]}
				>
					{titleCase(t('T_SITE_RANKING_PLAYER', { defaultValue: 'Player' }))}
				</Text>
				<Text style={[styles.headerText, { fontSize: 12 * scale, lineHeight: 14 * scale }]}>
					{metricLabel}
				</Text>
			</View>
			<View
				style={[styles.headerDivider, { marginHorizontal: 8 * scale, marginVertical: 4 * scale }]}
			/>
			{scrolls ? (
				<IndicatorScrollView
					style={styles.rowsScroll}
					contentContainerStyle={styles.rowsContent}
					showsVerticalScrollIndicator={false}
				>
					{body}
				</IndicatorScrollView>
			) : (
				<View style={styles.rowsContent}>{body}</View>
			)}
		</View>
	);
};

const RankingRows = ({
	rows,
	scale,
	valueIcon,
	valueWidth,
	emptyText,
}: {
	rows: RankingRow[];
	scale: number;
	valueIcon: number;
	valueWidth: number;
	emptyText: string;
}) => (
	<>
		{rows.map(row => (
			<RankingRowView
				key={row.id}
				row={row}
				scale={scale}
				valueIcon={valueIcon}
				valueWidth={valueWidth}
			/>
		))}
		{rows.length === 0 ? (
			<Text style={[styles.emptyRankings, { fontSize: 14 * scale }]}>{emptyText}</Text>
		) : null}
	</>
);

/** "Avatar / Leaderboard": a 38 disc with a gold rim and an orange glow. */
const AVATAR = { size: 38, rim: 1.5, glow: 5 } as const;

const RankingRowView = ({
	row,
	scale,
	valueIcon,
	valueWidth,
}: {
	row: RankingRow;
	scale: number;
	valueIcon: number;
	valueWidth: number;
}) => {
	const medal =
		row.rank === 1
			? RankingsFirstPlacePng
			: row.rank === 2
				? RankingsSecondPlacePng
				: row.rank === 3
					? RankingsThirdPlacePng
					: null;
	const avatar = row.avatarFileId ? { uri: getAvatarUrl(row.avatarFileId) } : PlayerIconPng;
	const isCurrentPlayer = row.isYou ?? row.username === 'YOU';
	const avatarSize = AVATAR.size * scale;
	const photoSize = avatarSize - 2 * AVATAR.rim * scale;

	return (
		<View
			style={[
				styles.rankRow,
				isCurrentPlayer && styles.currentPlayerRow,
				{
					height: 55 * scale,
					marginTop: 6 * scale,
					borderRadius: 8 * scale,
					paddingHorizontal: 10 * scale,
				},
				isCurrentPlayer && { boxShadow: `0px 0px ${5 * scale}px 0px #FF7200` },
			]}
		>
			{/* The other rows are the flat surface/raised; yours is the red gradient. */}
			{isCurrentPlayer ? (
				<View pointerEvents='none' style={StyleSheet.absoluteFill}>
					<Svg width='100%' height='100%'>
						<Defs>
							<LinearGradient id={`rankingRow-${row.id}`} x1='0' y1='0' x2='0' y2='1'>
								<Stop offset='0' stopColor='#770700' />
								<Stop offset='0.8' stopColor='#320400' />
							</LinearGradient>
						</Defs>
						<Rect x='0' y='0' width='100%' height='100%' fill={`url(#rankingRow-${row.id})`} />
					</Svg>
				</View>
			) : null}
			<View style={[styles.rankCell, { width: 24 * scale, height: 28 * scale }]}>
				{medal ? (
					<Image
						source={medal}
						resizeMode='contain'
						style={{ width: 24 * scale, height: 28 * scale }}
					/>
				) : null}
				{!medal ? (
					<Text
						style={[
							styles.rankText,
							isCurrentPlayer && styles.currentPlayerRank,
							{ fontSize: (isCurrentPlayer ? 14 : 16) * scale, lineHeight: 20 * scale },
						]}
					>
						{row.rank}
					</Text>
				) : null}
			</View>
			<View
				style={[
					styles.avatarRing,
					{
						width: avatarSize,
						height: avatarSize,
						borderRadius: avatarSize / 2,
						borderWidth: AVATAR.rim * scale,
						marginLeft: 18 * scale,
						boxShadow: `0px 0px ${AVATAR.glow * scale}px 0px rgba(255, 114, 0, 0.7)`,
					},
				]}
			>
				<Image
					source={avatar}
					style={{ width: photoSize, height: photoSize, borderRadius: photoSize / 2 }}
				/>
				{row.isVip ? (
					<Image
						source={CrownPng}
						resizeMode='contain'
						style={[
							styles.avatarCrown,
							{
								width: 17 * scale,
								height: 15 * scale,
								top: -8 * scale,
								left: '50%',
								transform: [{ translateX: -8.5 * scale }],
							},
						]}
					/>
				) : null}
			</View>
			<View style={[styles.playerCell, { marginLeft: 14 * scale }]}>
				<Text
					numberOfLines={1}
					style={[styles.playerName, { fontSize: 14 * scale, lineHeight: 16 * scale }]}
				>
					{row.username}
				</Text>
				<Text
					numberOfLines={1}
					style={[styles.clubName, { fontSize: 12 * scale, lineHeight: 14 * scale }]}
				>
					{titleCase(row.club)}
				</Text>
			</View>
			<View style={[styles.chipCell, { width: (30 + valueWidth) * scale }]}>
				<Image
					source={valueIcon}
					resizeMode='contain'
					style={{ width: 24 * scale, height: 24 * scale }}
				/>
				<Text
					numberOfLines={1}
					adjustsFontSizeToFit
					minimumFontScale={0.7}
					style={[
						styles.chipValue,
						{ width: valueWidth * scale, fontSize: 16 * scale, lineHeight: 20 * scale },
					]}
				>
					{formatChips(row.totalChips)}
				</Text>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	duelNote: {
		color: '#FFF5EB',
		fontWeight: '600',
		textAlign: 'center',
	},
	screen: { flex: 1, minHeight: 0 },
	fixedContent: { alignSelf: 'center', alignItems: 'center' },
	desktopScroll: { flex: 1 },
	desktopColumn: { alignItems: 'center' },
	// border/gold
	heroFrame: { borderColor: '#E89200', overflow: 'hidden' },
	heroImage: { position: 'absolute' },
	heroCopy: {
		alignItems: 'flex-end',
		bottom: 0,
		justifyContent: 'center',
		position: 'absolute',
		top: 0,
	},
	// gold/200, with the file's 0 1 4 rgba(38, 0, 0, 0.8) text shadow.
	heroTitleText: {
		color: '#FFD766',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
		textAlign: 'right',
		textShadowColor: 'rgba(38, 0, 0, 0.8)',
		textShadowOffset: { width: 0, height: 1 },
		textShadowRadius: 4,
	},
	// text/accent
	heroTagline: {
		color: '#FFFF99',
		fontFamily: 'Sofia Sans',
		fontWeight: '500',
		textAlign: 'right',
	},
	tabs: {
		alignItems: 'center',
		flexDirection: 'row',
		overflow: 'hidden',
		borderColor: '#6B1A10',
		borderRadius: 10,
		borderWidth: 1,
	},
	tabSlot: { alignItems: 'center', flex: 1, flexDirection: 'row', height: '100%' },
	// Off-screen copies of the label words, laid out at their one-line width to be measured.
	tabMeasure: { alignItems: 'flex-start', left: 0, opacity: 0, position: 'absolute', top: 0 },
	tab: {
		alignItems: 'center',
		flex: 1,
		justifyContent: 'center',
		borderRadius: 8,
		gap: 4,
	},
	// The rim is drawn by ActiveTabPlate; this keeps the glow.
	tabActive: {
		shadowColor: '#FF7200',
		shadowOpacity: 0.45,
		shadowRadius: 16,
	},
	tabDivider: { width: 1, backgroundColor: '#5C2416' },
	tabLabel: {
		color: '#D9C4BC',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
		textAlign: 'center',
	},
	tabLabelActive: { color: '#FFFF99' },
	tabLabelBox: { alignSelf: 'stretch', justifyContent: 'center' },
	// surface/sunken, border/red
	periodTabs: {
		backgroundColor: '#210503',
		borderColor: '#6B1A10',
		borderWidth: 1,
		flexDirection: 'row',
	},
	periodTab: { alignItems: 'center', flex: 1, justifyContent: 'center' },
	periodLabel: { color: '#D9C4BC', fontFamily: 'Sofia Sans', fontWeight: '700' },
	// gold/50
	periodLabelActive: { color: '#FFF5B9' },
	leagues: {
		alignItems: 'flex-start',
		borderColor: '#6B1A10',
		borderRadius: 12,
		borderWidth: 1,
		flexDirection: 'row',
		overflow: 'hidden',
	},
	league: {
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 10,
		paddingBottom: 4,
		paddingTop: 2,
	},
	// The fill and rim are drawn by FilledPlate; this keeps the glow.
	leagueActive: {
		shadowColor: '#FFB21A',
		shadowOpacity: 0.55,
		shadowRadius: 2.5,
		elevation: 3,
	},
	leagueName: { color: '#FFF5B9', fontFamily: 'Sofia Sans', fontWeight: '700', marginTop: -5 },
	leagueNameActive: { color: '#FFFF99' },
	leagueRange: { color: '#A88E86', fontFamily: 'Sofia Sans', fontWeight: '500', marginTop: -1 },
	leagueRangeActive: { color: '#FFD766' },
	// surface/sunken; the rim colour comes per category.
	table: {
		alignSelf: 'center',
		backgroundColor: '#210503',
		borderWidth: 1,
		flex: 1,
		minHeight: 0,
		overflow: 'hidden',
	},
	tableGrows: { flexGrow: 0, flexShrink: 0, flexBasis: 'auto' },
	tableHeader: { alignItems: 'center', flexDirection: 'row', gap: 8 },
	headerText: {
		color: '#A88E86',
		fontFamily: 'Sofia Sans',
		fontWeight: '600',
		lineHeight: 14,
	},
	rankHeader: { textAlign: 'center' },
	playerHeader: { textAlign: 'left' },
	headerDivider: { backgroundColor: '#5C2416', height: 1 },
	rowsScroll: { flex: 1, minHeight: 0, width: '100%' },
	rowsContent: { paddingBottom: 2 },
	emptyRankings: {
		color: '#A88E86',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
		paddingVertical: 22,
		textAlign: 'center',
	},
	// surface/raised
	rankRow: {
		alignItems: 'center',
		backgroundColor: '#330E07',
		flexDirection: 'row',
		borderColor: '#DA600E',
		borderWidth: 1,
		overflow: 'hidden',
	},
	currentPlayerRow: { backgroundColor: '#000000', borderColor: '#FCF17B' },
	rankCell: { alignItems: 'center', justifyContent: 'center' },
	rankText: {
		color: '#D9C4BC',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
		position: 'absolute',
		lineHeight: 20,
	},
	currentPlayerRank: { color: '#FFF5B9', fontSize: 14 },
	avatarRing: {
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: '#4D2C2A',
		borderColor: '#FCF17B',
	},
	avatarCrown: { position: 'absolute', zIndex: 1 },
	playerCell: { flex: 1, minWidth: 0 },
	playerName: { color: '#FFF5B9', fontFamily: 'Sofia Sans', fontWeight: '700', lineHeight: 16 },
	clubName: { color: '#A88E86', fontFamily: 'Sofia Sans', fontWeight: '500', lineHeight: 14 },
	chipCell: { alignItems: 'center', flexDirection: 'row', gap: 6, justifyContent: 'flex-end' },
	chipValue: {
		color: '#FFFF99',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
		lineHeight: 20,
		textAlign: 'right',
	},
});

export default RankingScreen;
